import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck2, Loader2, RefreshCw, Unlink, CalendarX2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("veloclin_token")}` });
const hm = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
const todayISO = () => new Date(Date.now() - 3 * 3600e3).toISOString().slice(0, 10);

const BusyList = ({ busy }) => (
  <div data-testid="gcal-busy-list" className="mt-3 space-y-1.5">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Bloqueado hoje pelo Google Agenda</p>
    {busy.length === 0 ? <p className="text-xs text-slate-400">Nenhum compromisso pessoal hoje.</p> : busy.map((b, i) => (
      <div key={i} data-testid={`gcal-busy-${i}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <CalendarX2 className="h-3.5 w-3.5 text-rose-500" />
        <span className="font-mono-num font-semibold">{b.all_day ? "Dia todo" : `${hm(b.start)}–${hm(b.end)}`}</span>
        <span className="truncate">{b.summary}</span>
      </div>
    ))}
  </div>
);

export const GoogleAgendaCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState([]);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/gcal/status`, { headers: auth() });
    const d = r.ok ? await r.json() : { connected: false };
    setSt(d);
    if (d.connected) {
      const b = await fetch(`${API}/api/gcal/busy?date=${todayISO()}`, { headers: auth() });
      if (b.ok) setBusy((await b.json()).busy || []);
    }
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("gcal");
    if (q === "ok") toast.success("Google Agenda conectado", { description: "Suas consultas e compromissos agora sincronizam automaticamente." });
    if (q === "erro") toast.error("Não foi possível conectar o Google Agenda", { description: "Tente novamente e autorize o acesso à agenda." });
    if (q) window.history.replaceState({}, "", window.location.pathname);
    load();
  }, [load]);

  const connect = async () => {
    setWorking(true);
    const r = await fetch(`${API}/api/gcal/connect`, { headers: auth() });
    if (!r.ok) { setWorking(false); return toast.error("Falha ao iniciar conexão com o Google"); }
    window.location.href = (await r.json()).url;
  };
  const act = async (path, okMsg) => {
    setWorking(true);
    try {
      const r = await fetch(`${API}/api/gcal/${path}`, { method: "POST", headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha na operação");
      toast.success(typeof okMsg === "function" ? okMsg(d) : okMsg);
      await load();
    } catch (e) { toast.error(e.message || "Falha na operação, tente novamente"); } finally { setWorking(false); }
  };

  if (!st) return null;
  return (
    <div data-testid="gcal-card" className="mb-4 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><CalendarCheck2 className="h-5 w-5" /></div>
          <div className="min-w-0">
            <h4 className="font-heading text-sm font-bold text-slate-900">Google Agenda</h4>
            <p data-testid="gcal-status" className="truncate text-xs text-slate-500">
              {st.connected ? `Conectado a ${st.email || "sua conta Google"} · sincronização nos dois sentidos` : st.expired ? "Conexão expirada — reconecte sua conta" : "Consultas vão para o Google e seus compromissos bloqueiam horários"}
            </p>
          </div>
        </div>
        {st.connected ? (
          <div className="flex gap-2">
            <button data-testid="gcal-sync-btn" disabled={working} onClick={() => act("sync-all", (d) => `${d.synced} consulta(s) enviadas ao Google Agenda`)} className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"><RefreshCw className="h-3.5 w-3.5" />Sincronizar</button>
            <button data-testid="gcal-disconnect-btn" disabled={working} onClick={() => act("disconnect", "Google Agenda desconectado")} className="flex items-center gap-1.5 rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"><Unlink className="h-3.5 w-3.5" />Desconectar</button>
          </div>
        ) : (
          <button data-testid="gcal-connect-btn" disabled={working} onClick={connect} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60">
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck2 className="h-4 w-4" />}Conectar Google Agenda
          </button>
        )}
      </div>
      {st.connected && <BusyList busy={busy} />}
    </div>
  );
};
